import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import LogoutButton from "@/components/LogoutButton";
import ThemeToggle from "@/components/ThemeToggle";
import CsaOnboardingTutorial from "@/components/CsaOnboardingTutorial";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  Play, Fuel, ClipboardCheck, PlaneTakeoff, AlertTriangle, FileText,
  ArrowRight, Loader2, User as UserIcon, Clock, Wallet, ChevronRight, CheckCircle2, XCircle, CalendarDays,
} from "lucide-react";

/**
 * CSA Home / Landing hub. Mobile-first tile grid.
 * Shift ops (check-in / active shift) live at /csa/shift.
 */
export default function CsaHome() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const nav = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pump, setPump] = useState(null);
  const [rejected, setRejected] = useState([]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get("/csa/me/summary"),
      api.get("/setup/state").catch(() => ({ data: {} })),
      api.get("/csa/rejected-hisabs").catch(() => ({ data: { items: [] } })),
    ])
      .then(([s, st, rj]) => {
        if (cancelled) return;
        setSummary(s.data);
        setPump(st.data?.pump || null);
        setRejected(rj.data?.items || []);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const reopenHisab = async (r) => {
    try {
      if (!r.in_progress) {
        // Only call reopen for freshly-rejected HISABs. Already-in-progress
        // ones are already DRAFT + reopened_at, so re-hitting reopen would
        // just refresh the timestamp — cheap but unnecessary.
        await api.post(`/hisab/${r.id}/reopen`);
        toast.success(t("csa.csahome.hisab_reopened_please_fix_and_resubmit"));
      } else {
        toast.info(t("csa.csahome.resuming_your_correction_finish_and_subm"));
      }
      setRejected(rejected.filter((x) => x.id !== r.id));
      // ?fix=<id> tells the CsaPortal + backend to load THIS specific HISAB,
      // even if the CSA already has an active check-in for a later shift.
      nav(`/csa/shift?fix=${encodeURIComponent(r.id)}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const activeShift = summary?.active_shift;
  const isCheckedIn = !!activeShift;
  const todayAtt = summary?.today_attendance;
  const shortage = summary?.open_shortage_this_month || 0;
  const pendingLeaves = summary?.pending_leaves || 0;
  const submitted = summary?.hisabs?.submitted || 0;
  const approved = summary?.hisabs?.approved || 0;

  // If a rejected HISAB has been reopened (in_progress) it MUST take
  // precedence over the live shift — otherwise tapping the big green
  // "Resume Morning" banner drops the CSA on their current shift's
  // auto-populated openings and hides the rejection entirely.
  const rejectedInProgress = rejected.find((r) => r.in_progress) || null;
  const shiftCtaTo = rejectedInProgress
    ? `/csa/shift?fix=${encodeURIComponent(rejectedInProgress.id)}`
    : "/csa/shift";

  return (
    <div className="min-h-screen bg-background pb-20" data-testid="csa-home-page">
      <CsaOnboardingTutorial />
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-border bg-card/95 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-2 px-4 py-3">
          <div className="flex items-center gap-2 min-w-0">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary" style={{ backgroundColor: pump?.theme_color ? `${pump.theme_color}22` : undefined }}>
              <UserIcon className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <div className="truncate text-sm font-bold text-foreground" data-testid="csa-name">{user?.name || "Salesman"}</div>
              <div className="truncate text-[10px] uppercase tracking-widest text-muted-foreground">{pump?.name || "HisabX"}</div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <LanguageSwitcher compact />
            <ThemeToggle />
            <LogoutButton where="csa-home" />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-5">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : (
          <>
            {/* Rejected HISABs — CSA must fix & resubmit */}
            {rejected.length > 0 && (
              <div className="mb-4 rounded-2xl border-2 border-red-300 bg-red-50/70 p-3 dark:border-red-900/60 dark:bg-red-950/40" data-testid="csa-rejected-banner">
                <div className="mb-2 flex items-center gap-2 text-red-800 dark:text-red-200">
                  <XCircle className="h-4 w-4" />
                  <div className="text-sm font-bold">{rejected.length} HISAB{rejected.length > 1 ? "s" : ""} need your attention — fix &amp; resubmit</div>
                </div>
                <div className="space-y-1">
                  {rejected.map((r) => (
                    <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 rounded border border-red-200 bg-card/80 px-2 py-1.5 dark:border-red-900/40" data-testid={`csa-rejected-${r.id}`}>
                      <div className="min-w-0 flex-1 text-[11px]">
                        <div className="font-mono font-bold text-foreground">{r.date} · Shift {r.shift}</div>
                        <div className="text-muted-foreground">
                          <span className={"rounded px-1.5 py-0.5 font-bold uppercase " + (r.in_progress ? "bg-amber-500/20 text-amber-800 dark:text-amber-200" : "bg-red-500/20 text-red-800 dark:text-red-200")}>
                            {r.in_progress ? "IN PROGRESS" : (r.reject_category || "REVIEW")}
                          </span>
                          {r.verification_reason ? ` · ${r.verification_reason}` : ""}
                        </div>
                      </div>
                      <button
                        onClick={() => reopenHisab(r)}
                        className={"rounded px-2 py-1 text-[11px] font-bold text-white " + (r.in_progress ? "bg-amber-600 hover:bg-amber-700" : "bg-red-600 hover:bg-red-700")}
                        data-testid={`csa-reopen-${r.id}`}
                      >
                        {r.in_progress ? "Resume" : "Fix now"}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* PRIMARY: Start / Resume Shift banner */}
            <button
              onClick={() => nav(shiftCtaTo)}
              className={
                "group relative w-full overflow-hidden rounded-2xl border-2 p-5 text-left transition-all hover:-translate-y-0.5 active:scale-[0.99] " +
                (rejectedInProgress
                  ? "border-amber-500/60 bg-gradient-to-br from-amber-500/15 to-amber-500/5 shadow-[0_10px_40px_-15px_rgba(217,119,6,0.5)]"
                  : isCheckedIn
                    ? "border-emerald-500/50 bg-gradient-to-br from-emerald-500/15 to-emerald-500/5 shadow-[0_10px_40px_-15px_rgba(16,185,129,0.5)]"
                    : "border-primary/40 bg-gradient-to-br from-primary/15 to-primary/5 shadow-[0_10px_40px_-15px_hsl(var(--primary)/0.5)]")
              }
              data-testid="csa-primary-shift-cta"
            >
              <div aria-hidden className="pointer-events-none absolute -right-6 -top-6 h-32 w-32 rounded-full bg-white/10 blur-2xl" />
              <div className="relative flex items-center gap-4">
                <div className={"flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl text-white " + (rejectedInProgress ? "bg-amber-500" : isCheckedIn ? "bg-emerald-500" : "bg-primary")}>
                  {rejectedInProgress ? <AlertTriangle className="h-7 w-7" /> : isCheckedIn ? <Fuel className="h-7 w-7" /> : <Play className="h-7 w-7" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">
                    {rejectedInProgress ? "Fix rejected HISAB first" : isCheckedIn ? "Shift in progress" : "Ready to start"}
                  </div>
                  <div className="mt-0.5 text-xl font-bold text-foreground">
                    {rejectedInProgress ? (
                      <>{t("csa.csahome.continue")} <span className="text-amber-600 dark:text-amber-400">{rejectedInProgress.date} · {rejectedInProgress.shift}</span></>
                    ) : isCheckedIn ? (
                      <>{t("csa.csahome.resume")} <span className="text-emerald-600 dark:text-emerald-400">{activeShift.shift_name || "shift"}</span></>
                    ) : (
                      "Start today's shift"
                    )}
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {rejectedInProgress
                      ? "Your live shift will be waiting once this rejection is resubmitted"
                      : isCheckedIn
                        ? `Checked in ${activeShift.checked_in_at ? new Date(activeShift.checked_in_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}`
                        : "Selfie + GPS · Allocate nozzles · Log expenses"}
                  </div>
                </div>
                <ArrowRight className="h-5 w-5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-1" />
              </div>
            </button>

            {/* Grid of stat / navigation tiles */}
            <div className="mt-4 grid grid-cols-2 gap-3">
              <TileLink
                to="/csa/attendance"
                testId="tile-attendance"
                icon={<ClipboardCheck className="h-5 w-5" />}
                title={t("csa.csahome.attendance")}
                caption={todayAtt ? `Today: ${(todayAtt.status || "PRESENT").toLowerCase()}` : "Mark today's attendance"}
                tone={todayAtt ? "ok" : "warn"}
              />
              <TileLink
                to="/csa/leaves"
                testId="tile-leaves"
                icon={<PlaneTakeoff className="h-5 w-5" />}
                title={t("csa.csahome.apply_leave")}
                caption={pendingLeaves > 0 ? `${pendingLeaves} pending` : "Take a day off"}
                tone={pendingLeaves > 0 ? "info" : "neutral"}
              />
              <TileLink
                to="/csa/shortages"
                testId="tile-shortages"
                icon={<AlertTriangle className="h-5 w-5" />}
                title={t("csa.csahome.shortages")}
                caption={shortage > 0 ? `₹${shortage.toLocaleString("en-IN")} this month` : "All clear ✓"}
                tone={shortage > 0 ? "bad" : "ok"}
                bigValue={shortage > 0 ? `₹${shortage.toLocaleString("en-IN")}` : null}
              />
              <TileLink
                to="/csa/shift"
                onClick={(e) => { e.preventDefault(); nav("/csa/shift"); }}
                testId="tile-hisabs"
                icon={<FileText className="h-5 w-5" />}
                title={t("csa.csahome.submitted_hisabs")}
                caption={`${approved} approved · ${submitted} pending`}
                tone="neutral"
                bigValue={String((summary?.hisabs?.approved || 0) + submitted)}
              />
            </div>

            {/* Quick actions row */}
            <div className="mt-4 rounded-xl border border-border bg-card p-3">
              <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{t("csa.csahome.quick_actions")}</div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <QuickAction to="/csa/attendance" testId="quick-att" icon={<CalendarDays className="h-4 w-4" />} label={t("csa.csahome.history")} />
                <QuickAction to="/csa/leaves" testId="quick-leave" icon={<PlaneTakeoff className="h-4 w-4" />} label={t("csa.csahome.new_leave")} />
                <QuickAction to={shiftCtaTo} testId="quick-shift" icon={<Fuel className="h-4 w-4" />} label={rejectedInProgress ? "Fix" : isCheckedIn ? "Resume" : "Start"} highlight />
              </div>
            </div>

            {/* Support line */}
            <p className="mt-5 text-center text-[10px] uppercase tracking-widest text-muted-foreground">
              HisabX · Track every drop
            </p>
          </>
        )}
      </main>
    </div>
  );
}

function TileLink({ to, testId, icon, title, caption, tone = "neutral", bigValue, onClick }) {
  const tones = {
    neutral: "border-border bg-card hover:border-primary/40",
    ok: "border-emerald-300/70 bg-emerald-50/50 hover:border-emerald-500 dark:border-emerald-900/60 dark:bg-emerald-950/30",
    warn: "border-amber-300/70 bg-amber-50/50 hover:border-amber-500 dark:border-amber-900/60 dark:bg-amber-950/30",
    bad: "border-red-300/70 bg-red-50/50 hover:border-red-500 dark:border-red-900/60 dark:bg-red-950/30",
    info: "border-sky-300/70 bg-sky-50/50 hover:border-sky-500 dark:border-sky-900/60 dark:bg-sky-950/30",
  };
  const iconTones = {
    neutral: "bg-primary/10 text-primary",
    ok: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
    warn: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
    bad: "bg-red-500/15 text-red-600 dark:text-red-400",
    info: "bg-sky-500/15 text-sky-600 dark:text-sky-400",
  };
  return (
    <Link
      to={to}
      onClick={onClick}
      className={"group relative flex flex-col justify-between rounded-xl border-2 p-3 transition-all hover:-translate-y-0.5 active:scale-[0.98] " + tones[tone]}
      data-testid={testId}
    >
      <div className="flex items-start justify-between">
        <div className={"flex h-9 w-9 items-center justify-center rounded-lg " + iconTones[tone]}>{icon}</div>
        <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 transition group-hover:opacity-100" />
      </div>
      <div className="mt-3">
        <div className="text-sm font-bold text-foreground">{title}</div>
        <div className="mt-0.5 text-[11px] text-muted-foreground">{caption}</div>
        {bigValue && (
          <div className="mt-1.5 font-mono text-xl font-bold tabular-nums text-foreground">{bigValue}</div>
        )}
      </div>
    </Link>
  );
}

function QuickAction({ to, testId, icon, label, highlight }) {
  return (
    <Link
      to={to}
      className={
        "flex flex-col items-center gap-1 rounded-lg border p-2.5 text-[11px] font-semibold transition " +
        (highlight
          ? "border-primary bg-primary/10 text-primary hover:bg-primary/20"
          : "border-border bg-background text-foreground hover:border-primary/50 hover:bg-primary/5")
      }
      data-testid={testId}
    >
      <div className={highlight ? "text-primary" : "text-muted-foreground"}>{icon}</div>
      {label}
    </Link>
  );
}
